import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  type ReactNode,
} from 'react'
import { emit } from '../platform/analytics/analytics'

/**
 * Exported because the settings picker has to render one option per member.
 * Left private, its only recourse would be to restate the three — the same
 * duplication this array exists to remove, across a file boundary where it is
 * harder to notice when a fourth is added.
 */
export const THEME_PREFERENCES = ['system', 'light', 'dark'] as const
export type ThemePreference = (typeof THEME_PREFERENCES)[number]
const KEY = 'tracke5.theme'

export type ThemeChangeSource = 'top_bar' | 'settings'

type Ctx = {
  preference: ThemePreference
  resolved: 'light' | 'dark'
  /** `via` is required so a new caller cannot skip the analytics source. */
  setPreference: (p: ThemePreference, via: ThemeChangeSource) => void
}
const ThemeContext = createContext<Ctx | null>(null)

/**
 * The stored value is outside our control — a key from an older scheme, another
 * tab, a hand-edited devtools entry. A bare `as ThemePreference` would make
 * `resolved` unsound and put an unrecognised string on `body[data-theme]`,
 * where it matches no selector in tokens.css. A predicate narrows without a
 * cast, and reading it off THEME_PREFERENCES rather than restating the members
 * keeps one list to maintain instead of two that must move together.
 */
function isPreference(value: string | null): value is ThemePreference {
  return THEME_PREFERENCES.some((preference) => preference === value)
}

/**
 * Storage throws SecurityError when cookies are blocked, inside a sandboxed
 * iframe, and under some enterprise policies. This provider wraps the whole
 * app with no error boundary above it, so an unguarded read is a white screen
 * for a user whose only sin is blocking cookies. Blocked storage should cost
 * the preference, not the session.
 */
function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(KEY)
    return isPreference(stored) ? stored : 'system'
  } catch {
    return 'system'
  }
}

function writePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(KEY, preference)
  } catch {
    // The choice still applies in memory for this session; it just will not
    // survive a reload. Nothing here is worth interrupting the user over.
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPref] = useState<ThemePreference>(readPreference)
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches,
  )

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    // The plan wrote `onChange as EventListener`, which does not compile: the
    // two signatures do not overlap, so TS2352 rejects the cast. `change` on a
    // MediaQueryList is already typed as MediaQueryListEvent, so the cast was
    // never needed — typing the parameter is both correct and cast-free.
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const resolved = preference === 'system' ? (systemDark ? 'dark' : 'light') : preference

  /**
   * A LAYOUT effect, not a passive one — #75.
   *
   * React flushes a child's effects before its parent's inside one commit, so
   * written passively this attribute lands after every descendant has already
   * run. Every token selected by `body[data-theme='dark']` then resolves to its
   * light value for the whole mount commit, and a consumer reading computed
   * style during it — `AppTexture` reading `--hex-ink` — bakes in the wrong
   * theme permanently, since `resolved` never changes again on its own.
   * `AppTexture` carried a `queueMicrotask` to dodge that; the cause was here.
   *
   * Layout effects for the whole tree flush before ANY passive effect and
   * before the browser paints, which covers both halves: consumers below see
   * the attribute, and the first painted frame is the resolved theme.
   *
   * It does not cover the frame before React runs at all — that is CSS-level
   * and precedes JS entirely, which is what the pre-paint script in
   * `index.html` exists for. The two read the same two sources, but they DO
   * disagree in one ordinary case: the script sets `html[data-pre-theme]` only
   * when the answer is dark and never clears it, so after an in-session toggle
   * to light, `html` still says dark while `body` says light. What makes that
   * safe is the `:not([data-theme='light'])` clause on the dark token block in
   * `tokens.css` — the pre-paint hint is deliberately written to lose to an
   * explicit attribute. Naming only the agreement, as an earlier draft of this
   * comment did, hides the half that is load-bearing.
   */
  useLayoutEffect(() => {
    document.body.setAttribute('data-theme', resolved)
  }, [resolved])

  const setPreference = (p: ThemePreference, via: ThemeChangeSource) => {
    if (p === preference) return
    writePreference(p)
    setPref(p)
    emit('theme_changed', { theme: p, surface: via })
  }

  return (
    <ThemeContext.Provider value={{ preference, resolved, setPreference }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): Ctx {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider')
  return ctx
}
